import { useFirstWeek } from '@/hooks/useConsoleData';
import { usePhone } from '@/hooks/usePhone';
import { Section } from '@/components/console/Section';
import {
  NOT_ENOUGH, channelLine, latestReadable, pctOf, rateHead, rateSample, weekLabel,
  type FirstWeekRow, type Rate,
} from '@/lib/growth-first-week';
import { NOT_MEASURED, num, when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * "First week", the top of Growth (GROWTH_OPERATING_SYSTEM_2026-10-21 section
 * 4; the first-week loop): the north star as two numbers per signup week, of
 * members who did a first check, the share with a second check within 48
 * hours and the share with three or more checks in their first seven
 * calendar days. Each number carries its sample (closed windows, and the ones
 * still open), a small bar when the server printed a percent, and the words
 * "not enough data" below the floor. Beneath it, the first read of the
 * outcome log: reminders sent in the last seven days and how many were
 * followed by a check within six hours, activations offered and done the same
 * day, routines started and finished.
 *
 * On a phone (under 640px) the table is one card per week with the same
 * numbers.
 */

function RateCell({ r, name }: { r: Rate; name: string }) {
  const p = pctOf(r);
  const sample = rateSample(r);
  return (
    <div data-rate={name}>
      <div style={{ fontWeight: 700 }}>{rateHead(r)}</div>
      <span className={s.relTrack} aria-hidden="true" data-bar={p === null ? 'none' : String(p)}>
        {p !== null && <span className={s.relFill} style={{ width: `${p}%` }} />}
      </span>
      {sample && <div className={s.sub}>{sample}</div>}
    </div>
  );
}

const rowLabel = (w: FirstWeekRow): string => (w.current ? `${weekLabel(w.weekStart)}, this week to date` : `week of ${weekLabel(w.weekStart)}`);

function WeekTable({ rows }: { rows: FirstWeekRow[] }) {
  return (
    <div className={s.card} style={{ padding: 0, overflowX: 'auto' }}>
      <table className={s.table} data-panel="first-week">
        <thead>
          <tr>
            <th>Signup week</th>
            <th style={{ textAlign: 'right' }}>Signed up</th>
            <th style={{ textAlign: 'right' }}>First check</th>
            <th>Second check within 48 h</th>
            <th>3 or more checks in the first 7 days</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.weekStart} data-week={w.weekStart} className={w.current ? s.rowOn : undefined}>
              <td>{rowLabel(w)}</td>
              <td style={{ textAlign: 'right' }}>{num(w.signups)}</td>
              <td style={{ textAlign: 'right' }}>{num(w.firstChecks)}</td>
              <td><RateCell r={w.secondCheckWithin48h} name="second" /></td>
              <td><RateCell r={w.threeChecksInFirst7Days} name="three" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WeekCards({ rows }: { rows: FirstWeekRow[] }) {
  return (
    <div className={s.list} data-panel="first-week-cards">
      {rows.map((w) => (
        <article key={w.weekStart} className={s.pCard} data-week={w.weekStart}>
          <div className={s.pCardTop}><strong>{rowLabel(w)}</strong></div>
          <dl className={s.pCardNums}>
            <div className={s.pCardNum}><dt>Signed up</dt><dd>{num(w.signups)}</dd></div>
            <div className={s.pCardNum}><dt>First check</dt><dd>{num(w.firstChecks)}</dd></div>
            <div className={s.pCardNum}><dt>Second check in 48 h</dt><dd><RateCell r={w.secondCheckWithin48h} name="second" /></dd></div>
            <div className={s.pCardNum}><dt>3+ checks, first 7 days</dt><dd><RateCell r={w.threeChecksInFirst7Days} name="three" /></dd></div>
          </dl>
        </article>
      ))}
    </div>
  );
}

function OutcomeCard({ label, value, sub, name }: { label: string; value: string; sub?: string; name: string }) {
  return (
    <div className={s.card} data-outcome={name}>
      <span className={s.cardLabel}>{label}</span>
      <span className={s.cardVal}>{value}</span>
      {sub && <span className={s.cardSub}>{sub}</span>}
    </div>
  );
}

export function FirstWeekPanel() {
  const q = useFirstWeek();
  const phone = usePhone();
  const d = q.data;
  const rows = d ? [d.current, ...d.weeks] : [];
  const readable = d ? latestReadable(d.weeks) : null;
  const o = d?.outcomes ?? null;

  const summary = d
    ? readable
      ? `week of ${weekLabel(readable.weekStart)}: ${rateHead(readable.secondCheckWithin48h)} second check within 48 h (n=${readable.secondCheckWithin48h.closed}) · read ${when(d.fetchedAt)}`
      : `${NOT_ENOUGH} yet: no signup week has ${d.minGroup} closed windows · read ${when(d.fetchedAt)}`
    : q.isError
      ? 'This panel needs GET /provider/admin/growth/first-week.'
      : 'Counting.';

  return (
    <Section title="First week" summary={summary} defaultOpen id="first-week">
      {d && (
        <div className={s.muted} style={{ fontSize: '0.78rem', marginBottom: 10 }}>
          Of members who did a first check, per signup week: a second check within 48 hours of the first, and 3 or more checks in their first 7 calendar days. A member counts once their own window has closed; a rate needs {d.minGroup} closed windows, below that it is the counts and the words. {num(d.excluded.team)} team and {num(d.excluded.qa)} QA accounts left out.
        </div>
      )}
      {d && (phone ? <WeekCards rows={rows} /> : <WeekTable rows={rows} />)}
      {q.isError && <div className={s.error}>GET /provider/admin/growth/first-week did not load. Nothing here is zero; the first week is {NOT_MEASURED} right now.</div>}

      {d && (
        <>
          <div className={s.groupTitle} style={{ marginTop: 16 }}>Outcomes, last {o?.days ?? 7} days</div>
          {o ? (
            <div className={s.cards} data-panel="outcomes">
              <OutcomeCard name="reminders" label="Reminders sent" value={num(o.reminders.count)} sub={`by channel: ${channelLine(o.reminders.byChannel)}`} />
              <OutcomeCard name="followed" label={`Followed by a check within ${o.reminders.windowHours} h`} value={rateHead(o.reminders.rate)} sub={rateSample(o.reminders.rate) || undefined} />
              <OutcomeCard name="offered" label="Activations offered" value={num(o.activationsOffered.count)} />
              <OutcomeCard name="done" label="Done the same day" value={rateHead(o.activationsOffered.rate)} sub={rateSample(o.activationsOffered.rate) || undefined} />
              <OutcomeCard name="routines" label="Routines started" value={num(o.routinesStarted.count)} sub={`finished the same day: ${rateHead(o.routinesStarted.rate)}${rateSample(o.routinesStarted.rate) ? `, ${rateSample(o.routinesStarted.rate)}` : ''}`} />
            </div>
          ) : (
            <div className={s.jWarn}>The outcome log could not be read this time. Nothing here is zero.</div>
          )}
          <div className={s.muted} style={{ fontSize: '0.78rem', marginTop: 8 }}>
            {d.definitions.reminder} {d.definitions.activation} Outcomes are filled nightly once each window has closed; until then they are still open, never a miss. Last filled: {when(o?.lastResolvedAt ?? null, 'not yet')}.
          </div>
          {d.unavailable.length > 0 && <div className={s.jWarn} style={{ marginTop: 10 }}>Not read this time: {d.unavailable.map((u) => u.source).join(', ')}. The counts leave those out rather than guess.</div>}
        </>
      )}
    </Section>
  );
}
